import { Button, Center, Group, Loader, Modal, Paper, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useDeleteFamily } from 'src/clients/hooks/useDeleteFamily';
import { useDeleteFamilyWithMembers } from 'src/clients/hooks/useDeleteFamilyWithMembers';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useReassignFamilyAdvisor } from 'src/clients/hooks/useReassignFamilyAdvisor';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { DropdownButton, DropdownButtonItem } from 'src/design-system/DropdownButton';
import { useConfirmationPopup } from 'src/design-system/useConfirmationPopup';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Logger } from 'src/utils/Logger';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { FamilyEmptyState } from './FamilyEmptyState';
import { FamilyMembersTable } from './FamilyMembersTable';

export function FamilyProfileContent() {
  const { client, isLoading } = useCurrentClient();

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  if (!client) {
    return null;
  }

  if (client.familyId) {
    return <Navigate to={`/family/${client.familyId}/profile?member=${client.id}`} replace />;
  }

  return (
    <Paper variant="content">
      <FamilyEmptyState firstName={client.firstName} />
    </Paper>
  );
}

export function FamilyProfilePanel({ familyId }: { familyId: string }) {
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { advisors } = useAdvisors();
  const {
    reassignFamilyAdvisor,
    isLoading: isReassigning,
    error: reassignError,
    reset: resetReassign,
  } = useReassignFamilyAdvisor();
  const { isAdvisor } = useCurrentUser();
  const {
    deleteFamily,
    isLoading: isDisbanding,
    error: disbandError,
    reset: resetDisband,
  } = useDeleteFamily();
  const {
    deleteFamilyWithMembers,
    isLoading: isDeletingMembers,
    error: deleteMembersError,
    reset: resetDeleteMembers,
  } = useDeleteFamilyWithMembers();
  const navigate = useNavigate();
  const { showConfirmation, confirmationElement } = useConfirmationPopup();
  const [pendingRemoval, setPendingRemoval] = useState<'disband' | 'delete' | null>(null);
  const [pendingAdvisorId, setPendingAdvisorId] = useState<string | null>(null);

  if (isFamilyLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  if (!family) {
    return (
      <Paper variant="content">
        <Text c="gray.6">This family could not be found.</Text>
      </Paper>
    );
  }

  function requestAdvisorChange(advisorId: string) {
    resetReassign();
    setPendingAdvisorId(advisorId);
  }

  async function confirmAdvisorChange() {
    if (!pendingAdvisorId) return;
    const advisor = advisors.find(a => a.id === pendingAdvisorId);
    const advisorName = advisor
      ? formatName(advisor.firstName, advisor.lastName, NameFormat.NATURAL)
      : 'the new advisor';
    try {
      await reassignFamilyAdvisor(familyId, pendingAdvisorId);
      setPendingAdvisorId(null);
      showConfirmation(`Advisor changed to ${advisorName}`);
    } catch (e) {
      Logger.error('Failed to change family advisor', e);
    }
  }

  function requestDisband() {
    resetDisband();
    setPendingRemoval('disband');
  }

  function requestDelete() {
    resetDeleteMembers();
    setPendingRemoval('delete');
  }

  async function confirmRemoval() {
    if (!family) return;
    const isDelete = pendingRemoval === 'delete';
    const successTitle = isDelete ? 'Family deleted' : 'Family disbanded';
    const successMessage = isDelete
      ? 'The family and all of its members have been deleted.'
      : 'The family has been disbanded. Its members remain as individual clients.';
    try {
      // The delete endpoint removes the family and every member in one
      // transaction, so a failure rolls back instead of orphaning members.
      await (isDelete ? deleteFamilyWithMembers(familyId) : deleteFamily(familyId));
      setPendingRemoval(null);
      notifications.show({ title: successTitle, message: successMessage, color: 'green' });
      navigate('/clients/families');
    } catch (e) {
      Logger.error('Failed to remove family', e);
    }
  }

  const familyMenuItems: DropdownButtonItem[] = [
    { label: 'Add member', onClick: () => navigate(`/family/${familyId}/add-member`) },
    { label: 'Manage roles', onClick: () => navigate(`/family/${familyId}/edit`) },
    { label: 'Change family name', onClick: () => navigate(`/family/${familyId}/edit`) },
    { label: 'Disband family', onClick: requestDisband },
    { label: 'Delete family and all members', onClick: requestDelete },
  ];

  const currentAdvisor = advisors.find(a => a.id === family.advisorUserId);
  const currentAdvisorName = currentAdvisor
    ? formatName(currentAdvisor.firstName, currentAdvisor.lastName, NameFormat.NATURAL)
    : 'Unassigned';
  const advisorItems: DropdownButtonItem[] = advisors
    .filter(a => a.id !== family.advisorUserId)
    .map(a => ({
      label: formatName(a.firstName, a.lastName, NameFormat.NATURAL),
      onClick: () => requestAdvisorChange(a.id),
    }));

  const pendingAdvisor = advisors.find(a => a.id === pendingAdvisorId);
  const pendingAdvisorName = pendingAdvisor
    ? formatName(pendingAdvisor.firstName, pendingAdvisor.lastName, NameFormat.NATURAL)
    : 'the new advisor';

  return (
    <Paper variant="content">
      <Group align="center" mb="lg" gap={24}>
        <Group gap="xs" align="center">
          <DropdownButton label="Edit family" items={familyMenuItems} ariaLabel="Family actions" />
        </Group>
        {!isAdvisor && (
          <Group gap={4} align="center">
            <Text
              c="gray.9"
              size="xs"
              fw={FONT_WEIGHTS.semibold}
              style={{ paddingTop: 3, paddingBottom: 3 }}
            >
              Advisor:
            </Text>
            <DropdownButton
              label={currentAdvisorName}
              items={advisorItems}
              ariaLabel="Change advisor"
            />
          </Group>
        )}
      </Group>
      <FamilyMembersTable familyId={familyId} />
      <ConfirmDeleteModal
        opened={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={confirmRemoval}
        title={pendingRemoval === 'delete' ? 'Delete family and all members' : 'Disband family'}
        message={
          pendingRemoval === 'delete'
            ? `This permanently deletes ${family.name} and all of its members. This cannot be undone.`
            : `This disbands ${family.name}. Its members are kept as individual clients.`
        }
        confirmLabel={pendingRemoval === 'delete' ? 'Delete family' : 'Disband family'}
        error={disbandError ?? deleteMembersError}
        isLoading={isDisbanding || isDeletingMembers}
      />
      <Modal
        opened={pendingAdvisorId !== null}
        onClose={() => setPendingAdvisorId(null)}
        title="Change advisor"
        centered
      >
        <Text mb="md">
          The whole family must share one advisor. Move everyone to {pendingAdvisorName}?
        </Text>
        {reassignError && (
          <Text c="red" mb="md">
            Could not change the advisor. Please try again.
          </Text>
        )}
        <Group justify="flex-end">
          <Button
            variant="default"
            onClick={() => setPendingAdvisorId(null)}
            disabled={isReassigning}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmAdvisorChange} loading={isReassigning}>
            Change advisor
          </Button>
        </Group>
      </Modal>
      {confirmationElement}
    </Paper>
  );
}
